import { Routes, Route, Navigate } from 'react-router-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import { BetaFeedbackWidget } from './components/BetaFeedbackWidget'
import { captureFeedbackContext } from './utils/betaFeedbackContext'
import { useAuth } from './hooks/useAuth'
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import MagicLoginPage from './pages/MagicLoginPage'
import OnboardingPage from './pages/OnboardingPage'
import ClubsPage from './pages/ClubsPage'
import AllEventsPage from './pages/AllEventsPage'
import ProfilePage from './pages/ProfilePage'
import ClubDetailPage from './pages/ClubDetailPage'
import CreateClubPage from './pages/CreateClubPage'
import ClubMembersPage from './pages/ClubMembersPage'
import ClubSettingsPage from './pages/ClubSettingsPage'
// import ClubEditPage from './pages/ClubEditPage' // Legacy - replaced by ClubEditRedirect
import ClubEventsPage from './pages/ClubEventsPage'
import EventDetailPage from './pages/EventDetailPage'
import EventCreatePage from './pages/EventCreatePage'
import GlobalEventCreatePage from './pages/GlobalEventCreatePage'
import EventEditPage from './pages/EventEditPage'
import EventCheckInPage from './pages/EventCheckInPage'
import QRScannerPage from './pages/QRScannerPage'
import PublicProfilePage from './pages/PublicProfilePage'
import ClubChatPage from './pages/ClubChatPage'
import UiPlayground from './pages/UiPlayground'
import InviteAcceptPage from './pages/InviteAcceptPage'
import WaitlistPage from './pages/WaitlistPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import AboutPage from './pages/AboutPage'
import FaqPage from './pages/FaqPage'
import ContactPage from './pages/ContactPage'
import AppHomePage from './pages/AppHomePage'
import AdminInvitesPage from './pages/AdminInvitesPage'
import PlatformAdminHomePage from './pages/PlatformAdminHomePage'
import PlatformAdminClubsPage from './pages/PlatformAdminClubsPage'
import PlatformAdminClubDetailPage from './pages/PlatformAdminClubDetailPage'
import NotFoundPage from './pages/NotFoundPage'
import LoadingPage from './pages/LoadingPage'
// import ProfileEditPage from './pages/ProfileEditPage' // Legacy - replaced by ProfileSettingsPage
import MemberDetailPage from './pages/MemberDetailPage'
import NotificationPreferencesPage from './pages/NotificationPreferencesPage'
// import SettingsPage from './pages/SettingsPage' // Legacy - replaced by SettingsHubPage
import SettingsHubPage from './pages/SettingsHubPage'
import ProfileSettingsPage from './pages/settings/ProfileSettingsPage'
import AccessibilitySettingsPage from './pages/settings/AccessibilitySettingsPage'
import NotificationSettingsPage from './pages/settings/NotificationSettingsPage'
import ClubEditRedirect from './pages/ClubEditRedirect'
import OfflineSyncStatus from './components/OfflineSyncStatus'
import { PWAManager } from './components/PWAManager' // Service worker only (no install prompts)
import { AuthModalProvider, useAuthModal } from './contexts/AuthModalContext'
import LoginModal from './components/LoginModal'
import OnboardingModal from './components/onboarding/OnboardingModal'

function AppContent() {
  const { isModalOpen, pendingNavigation, closeLoginModal } = useAuthModal();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Determine if we should show onboarding modal (Issue #563)
  const shouldShowOnboarding = 
    user && 
    !user.onboarding_completed && 
    !!user.onboarding_context;

  // Handle beta feedback widget click
  const handleFeedbackClick = () => {
    // Simple analytics
    interface WindowWithAnalytics extends Window {
      analytics?: {
        track: (event: string, properties: Record<string, unknown>) => void;
      };
    }
    const windowWithAnalytics = window as WindowWithAnalytics;
    
    if (windowWithAnalytics.analytics?.track) {
      windowWithAnalytics.analytics.track('beta_feedback_widget_clicked', {
        page: location.pathname,
        user_id: user?.id
      });
    }
    
    // Navigate with minimal context
    if (user) {
      const context = captureFeedbackContext(user);
      navigate('/contact', {
        state: {
          source: 'beta_widget',
          context
        }
      });
    } else {
      navigate('/contact');
    }
  };

  return (
    <>
      <OfflineSyncStatus />
      <PWAManager />
      <Routes>
        {/* HomePage and WaitlistPage handled outside Layout to control navigation visibility */}
        <Route path="/" element={<HomePage />} />
        <Route path="waitlist" element={<WaitlistPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="contact" element={<ContactPage />} />
        
        <Route path="/" element={<Layout />}>
          {/* Public routes */}
          <Route path="login" element={<LoginPage />} />
          <Route path="magic-login" element={<MagicLoginPage />} />
          <Route path="invite/:code" element={<InviteAcceptPage />} />
        <Route path="admin/invites" element={<ProtectedRoute><AdminInvitesPage /></ProtectedRoute>} />
        <Route path="platform-admin/home" element={<ProtectedRoute><PlatformAdminHomePage /></ProtectedRoute>} />
        <Route path="platform-admin/clubs" element={<ProtectedRoute><PlatformAdminClubsPage /></ProtectedRoute>} />
        <Route path="platform-admin/clubs/:slug" element={<ProtectedRoute><PlatformAdminClubDetailPage /></ProtectedRoute>} />
        <Route path="onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
        <Route path="home" element={<ProtectedRoute><AppHomePage /></ProtectedRoute>} />
        <Route path="clubs" element={<ProtectedRoute><ClubsPage /></ProtectedRoute>} />
        <Route path="events" element={<ProtectedRoute><AllEventsPage /></ProtectedRoute>} />
        <Route path="events/create" element={<ProtectedRoute><GlobalEventCreatePage /></ProtectedRoute>} />
        <Route path="me" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        {/* Legacy profile edit - redirect to new settings */}
        <Route path="me/edit" element={<Navigate to="/settings/profile" replace />} />
        <Route path="me/notifications" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
        {/* New Settings Hub & Sub-Pages */}
        <Route path="settings" element={<ProtectedRoute><SettingsHubPage /></ProtectedRoute>} />
        <Route path="settings/profile" element={<ProtectedRoute><ProfileSettingsPage /></ProtectedRoute>} />
        <Route path="settings/accessibility" element={<ProtectedRoute><AccessibilitySettingsPage /></ProtectedRoute>} />
        <Route path="settings/notifications" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
        {/* Old SettingsPage temporarily kept for reference - will be deleted after Pass 3 */}
        {/* <Route path="settings-old" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} /> */}
        <Route path="u/:id" element={<PublicProfilePage />} />
        <Route path="member/:id" element={<ProtectedRoute><MemberDetailPage /></ProtectedRoute>} />
        <Route path="playground" element={<UiPlayground />} />
        
        {/* Protected routes - require authentication */}
        <Route path="clubs/create" element={<ProtectedRoute><CreateClubPage /></ProtectedRoute>} />
        <Route path="clubs/:slug" element={<ProtectedRoute><ClubDetailPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/members" element={<ProtectedRoute><ClubMembersPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/settings" element={<ProtectedRoute><ClubSettingsPage /></ProtectedRoute>} />
        {/* Legacy club edit - redirect with wrapper for dynamic slug */}
        <Route path="clubs/:slug/edit" element={<ProtectedRoute><ClubEditRedirect /></ProtectedRoute>} />
        <Route path="clubs/:slug/chat" element={<ProtectedRoute><ClubChatPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/events" element={<ProtectedRoute><ClubEventsPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/events/create" element={<ProtectedRoute><EventCreatePage /></ProtectedRoute>} />
        <Route path="event/:id" element={<EventDetailPage />} />
        <Route path="event/:id/check-in" element={<ProtectedRoute><EventCheckInPage /></ProtectedRoute>} />
        <Route path="event/:id/edit" element={<ProtectedRoute><EventEditPage /></ProtectedRoute>} />
        <Route path="scan-qr" element={<ProtectedRoute><QRScannerPage /></ProtectedRoute>} />
        <Route path="loading" element={<LoadingPage />} />
        {/* More routes will be added as we build features */}
        
        {/* Catch-all route for 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      </Routes>
      
      <LoginModal
        isOpen={isModalOpen}
        onClose={closeLoginModal}
        pendingNavigation={pendingNavigation}
      />

      {/* Onboarding Modal (Issue #563) */}
      {shouldShowOnboarding && (
        <OnboardingModal
          user={user}
          context={user.onboarding_context!}
        />
      )}
      
      {/* Beta Feedback Widget */}
      <BetaFeedbackWidget onFeedbackClick={handleFeedbackClick} />
    </>
  )
}

function App() {
  return (
    <AuthModalProvider>
      <AppContent />
    </AuthModalProvider>
  )
}

export default App
